import routeService from '@/services/route-service'
import { setPopup, popup } from '@/services/map-service'
import routeWaypointPopup from '@/components/route-builder/route-waypoint-popup'
import { createApp } from 'vue'
import { STROKE_WEIGHT_MAP } from '@/lib/markup-styles'
import { POINT_COLORS } from '@/lib/mapbox/route-mode-helpers-constants'

/**
 * Returns GeoJSON for the route path.
 * @returns {GeoJSON} geojson on the route
 */
export function getRouteGeoJson() {
  // Apply the current route geometry to the geoJson for the route.
  const { route } = routeService
  if (route) {
    return route.geoJson
  }
}

/**
 * Returns GeoJSON for the preview path.
 * @param {string} routeColor - color of the route
 * @param {string} routeStyle - style of the route
 * @param {string} routeWeight - weight of the route
 * @param {boolean} dragPreview - whether this is a preview segment or not
 * @returns {GeoJSON} geojson of the route preview
 */
export function getPreviewGeoJson(
  routeColor,
  routeStyle,
  routeWeight,
  dragPreview
) {
  const { previewSegment } = routeService

  if (previewSegment) {
    return {
      type: 'Feature',
      geometry: previewSegment.geoJson,
      properties: {
        meta: 'route-preview',
        route_color: '#FF0000',
        route_style: 'solid',
        route_weight: STROKE_WEIGHT_MAP.get('normal').weight,
        highlight_opacity: dragPreview ? 1 : 0,
      },
    }
  }
}

/**
 * Returns markers for every waypoint.
 * @param {boolean} isDragging - whether the route is currently being dragged
 * @param {string} color - color of the route, used to match the points
 * @returns {Array} list of route points
 */
export function getWaypointMarkerGeoJson(isDragging, color) {
  const { waypoints } = routeService
  const selectedIndex = routeService.focusedWaypointIndex
  return waypoints.reduce((markers, location, index) => {
    // skip a selected point when dragging
    if (!isDragging || index !== selectedIndex) {
      let colorInner = color ?? POINT_COLORS.DEFAULT
      if (index === 0) {
        colorInner = POINT_COLORS.START
      } else if (index === waypoints.length - 1) {
        colorInner = POINT_COLORS.END
      }

      markers.push(_generatePoint({ colorInner, location, isDragging, index }))
    }
    return markers
  }, [])
}

/**
 * Returns markers for start and end points
 * @param {boolean} isDragging - whether the route is currently being dragged
 * @returns {Array} start and end point features
 */
export function getEndpointsMarkerGeoJson(isDragging) {
  const { waypoints } = routeService
  const endIndex = waypoints.length - 1

  return [
    _generatePoint({
      colorInner: POINT_COLORS.START,
      location: waypoints[0],
      isDragging,
      index: 0,
    }),
    _generatePoint({
      colorInner: POINT_COLORS.END,
      location: waypoints[endIndex],
      isDragging,
      index: endIndex,
    }),
  ]
}

/**
 * Create a point feature with the onX route properties
 * @param {string} colorInner - color of the route point
 * @param {Array} location - coordinate of the point
 * @param {number} index - position in the route points list
 * @returns {object} a point feature
 */
export function _generatePoint({ colorInner, location, index }) {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: location.toDegreesTuple(),
    },
    properties: {
      index,
      type: 'route-waypoint',
      meta: 'route-waypoint',
      colorInner,
      opacity: 1,
    },
  }
}

/**
 * Displays an interactive route waypoint popup on the map
 * @param {Array} coord - [lon, lat] coordinate to display the popup at
 */
export function showRouteWaypointPopup(coord) {
  const component = createApp(routeWaypointPopup)

  setPopup({
    component,
    coord: coord,
    className: 'markup-waypoint-delete',
    anchor: 'bottom',
    offset: 3,
  })
}

/**
 * Removes the route waypoint popup from the map
 * @param {Boolean} removeFocus - whether or not to clear the focused waypoint state in the route service
 */
export function removeRouteWaypointPopup(removeFocus = true) {
  if (popup()) {
    if (removeFocus) {
      routeService.focusedWaypointIndex = null
    }
    setPopup(null)
  }
}
